import { FormattedMessage, useIntl } from 'react-intl';
import { Row, Col, Form, Input, Select, FormInstance, Alert } from 'antd';

import FloatingFormItem from '@/components/_common/FloatingFormItem';
import {
  CnpjFormField,
  CpfFormField,
  InputText,
  PlanAccountsSelect,
} from '@/components/_common';
import {
  Supplier,
  SupplierClassification,
  SupplierType,
} from '@/core/domain/people/Suppliers/Suppliers.types';
import {
  PlanAccountDestination,
  PlanAccountHierarchy,
} from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.types';
import { useEffect } from 'react';
import { useImportedCpfCnpj } from '@/core/domain/people/ImportedCPFCNPJ/ImportedCPFCNPJ.hook';
import validateCpf from '@/utils/functions/validateCpf';
import { validateCnpj } from '@/utils/functions/validateCnpj';
import { useDebounce } from '@/hooks';
import { useSupplierFormContext } from '../SupplierFormProvider';
import { removeCpfCnpjMask } from '@/utils/mask/removeMask';

const defaultEmptyFieldError = {
  required: true,
  message: <FormattedMessage id="commons.form.errors.requiredField" />,
};

interface Props {
  form: FormInstance<Supplier>;
  isEdit?: boolean;
}

function SuppliersGeneralInfoForm({ form, isEdit = false }: Props) {
  const { Option } = Select;
  const intl = useIntl();

  const { supplier } = useSupplierFormContext();

  const selectedPersonType = Form.useWatch('type', form);

  const { mutateAsync } = useImportedCpfCnpj();

  const cpf = Form.useWatch('cpf', form);
  const cnpj = Form.useWatch('cnpj', form);
  const debounce = useDebounce(cpf || cnpj);

  const handleType = () => {
    form.resetFields(['cpf', 'cnpj', 'name', 'stateRegistration', 'nickName']);
  };

  useEffect(() => {
    if (!isEdit) {
      handleType();
    }
  }, [selectedPersonType, isEdit]);

  const handleCpfCnpj = async (value: string | undefined) => {
    const mask = removeCpfCnpjMask(value);
    const isValidCpf = validateCpf(mask);
    const isValidCnpj = validateCnpj(mask);

    if (isEdit) return;

    if (isValidCpf || isValidCnpj) {
      const data = await mutateAsync(mask);

      form.setFieldsValue({
        name: isValidCpf ? data.name : data.officialName,
        stateRegistration: data.stateRegistration,
        nickName: data.tradeName,
      });
    }
  };

  useEffect(() => {
    handleCpfCnpj(debounce);
  }, [debounce]);

  const supplierIsGlobal = !supplier?.matrixId && isEdit;

  return (
    <Form layout="vertical" form={form} name="supplierGeneral" preserve={false}>
      <Row gutter={[16, 8]} style={{ marginBottom: 12 }}>
        {supplierIsGlobal && (
          <Col span={24}>
            <Alert
              showIcon
              type="warning"
              description="Este fornecedor é global e não pode ser editado!"
            />
          </Col>
        )}

        <Col span={24} md={16} lg={8}>
          <FloatingFormItem
            label={<FormattedMessage id="suppliers.general.form.type" />}
            name="type"
            initialValue={SupplierType.Individual}
            getValueFromEvent={(value) => value}
          >
            <Select disabled={supplierIsGlobal}>
              <Option value={SupplierType.Individual}>
                <FormattedMessage id="suppliers.general.form.type.individual" />
              </Option>

              <Option value={SupplierType.LegalPerson}>
                <FormattedMessage id="suppliers.general.form.type.legalPerson" />
              </Option>
            </Select>
          </FloatingFormItem>
        </Col>
      </Row>

      <Row gutter={[16, 8]}>
        {selectedPersonType === SupplierType.LegalPerson && (
          <Col xs={24} sm={12} md={8}>
            <CnpjFormField
              fieldName="cnpj"
              required={false}
              disabled={supplierIsGlobal}
            />
          </Col>
        )}

        {selectedPersonType === SupplierType.Individual && (
          <Col xs={24} sm={12} md={8}>
            <CpfFormField
              fieldName="cpf"
              required={false}
              disabled={supplierIsGlobal}
            />
          </Col>
        )}

        <Col xs={24} sm={12} md={8}>
          <FloatingFormItem
            label={<FormattedMessage id="suppliers.general.form.name" />}
            name="name"
            rules={[defaultEmptyFieldError]}
          >
            <Input disabled={supplierIsGlobal} />
          </FloatingFormItem>
        </Col>

        {selectedPersonType === SupplierType.LegalPerson && (
          <>
            <Col xs={24} sm={12} md={8}>
              <FloatingFormItem
                label={
                  <FormattedMessage id="suppliers.general.form.nickName" />
                }
                name="nickName"
              >
                <Input disabled={supplierIsGlobal} />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <FloatingFormItem
                label={
                  <FormattedMessage id="suppliers.general.form.stateRegistration" />
                }
                name="stateRegistration"
              >
                <InputText onlyNumber disabled={supplierIsGlobal} />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <FloatingFormItem
                label={
                  <FormattedMessage id="suppliers.general.form.municipalRegistration" />
                }
                name="municipalRegistration"
              >
                <Input disabled={supplierIsGlobal} />
              </FloatingFormItem>
            </Col>
          </>
        )}

        <Col xs={24} sm={12} md={8}>
          <FloatingFormItem
            label={
              <FormattedMessage id="suppliers.general.form.classification" />
            }
            name="classification"
            rules={[defaultEmptyFieldError]}
            initialValue={SupplierClassification.Others}
          >
            <Select disabled={supplierIsGlobal}>
              <Option value={SupplierClassification.Dealership}>
                <FormattedMessage id="suppliers.general.form.classification.dealership" />
              </Option>

              <Option value={SupplierClassification.Distributor}>
                <FormattedMessage id="suppliers.general.form.classification.distributor" />
              </Option>

              <Option value={SupplierClassification.Retail}>
                <FormattedMessage id="suppliers.general.form.classification.retail" />
              </Option>

              <Option value={SupplierClassification.Wholesale}>
                <FormattedMessage id="suppliers.general.form.classification.wholesale" />
              </Option>

              <Option value={SupplierClassification.Others}>
                <FormattedMessage id="suppliers.general.form.classification.others" />
              </Option>
            </Select>
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <FloatingFormItem
            label={<FormattedMessage id="suppliers.general.form.email" />}
            name="email"
          >
            <Input disabled={supplierIsGlobal} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <FloatingFormItem
            label={<FormattedMessage id="suppliers.general.form.webSite" />}
            name="webSite"
          >
            <Input disabled={supplierIsGlobal} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={12}>
          <FloatingFormItem
            label={<FormattedMessage id="suppliers.general.form.product" />}
            name="product"
          >
            <Input disabled={supplierIsGlobal} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={12}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'suppliers.general.form.planAccount',
            })}
            name="planAccountId"
          >
            <PlanAccountsSelect
              queryFilter={{
                active: true,
                hierarchy: PlanAccountHierarchy.Analitical,
                destination: PlanAccountDestination.Expense,
              }}
              disabled={supplierIsGlobal}
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={24}>
          <FloatingFormItem
            name="note"
            label={<FormattedMessage id="suppliers.general.form.note" />}
          >
            <Input.TextArea
              rows={3}
              style={{ height: '102px' }}
              disabled={supplierIsGlobal}
            />
          </FloatingFormItem>
        </Col>
      </Row>
    </Form>
  );
}

export default SuppliersGeneralInfoForm;
